import Dexie, { Table } from "dexie";
import { Survey } from "../features/surveys/survey-types";
import { SurveyResults } from "../features/survey-results/survey-results-types";
import { SurveyAnalysis } from "@/features/analyses/survey-analyses-types";
import { Audience } from "@/features/audiences/audience-types";

class StorageService {
  private db: Dexie;

  constructor() {
    this.db = this.createDatabase();
  }

  createDatabase() {
    this.db = new Dexie("myDatabase");
    this.db.version(3).stores({
      audiences: "id",
      surveys: "id",
      survey_results: "surveyid", // Warning, gets confused with casing, don't add capital letters without thinking
      analyses: "surveyid",
    });
    return this.db;
  }

  async resetDatabase(): Promise<void> {
    await this.db.delete();
    this.createDatabase();
  }

  // Surveys

  // helper to provide better type safety
  surveys(): Table<Survey, string, Survey> {
    return this.db.table("surveys");
  }

  async getAllSurveys(): Promise<Survey[]> {
    const surveys = await this.surveys().toArray();
    console.info("Get all surveys", surveys);
    return surveys;
  }

  async getSurveyById(surveyId: string): Promise<Survey | undefined> {
    return await this.surveys().get(surveyId);
  }

  async saveSurvey(survey: Survey): Promise<void> {
    console.info("Saving survey", survey);
    await this.surveys().put(survey);
  }

  async deleteSurvey(surveyId: string): Promise<void> {
    await this.surveys().delete(surveyId);
    await this.survey_results().delete(surveyId);
    await this.analyses().delete(surveyId);
  }

  // Survey results

  // helper to provide better type safety
  survey_results(): Table<SurveyResults, string, SurveyResults> {
    return this.db.table("survey_results");
  }

  async saveSurveyResults(results: SurveyResults): Promise<void> {
    console.info("Saving survey results", results);
    await this.survey_results().put(results);
  }

  async getResultsBySurveyId(
    surveyId: string
  ): Promise<SurveyResults | undefined> {
    return await this.survey_results().get(surveyId);
  }

  // Analyses

  // helper to provide better type safety
  analyses(): Table<SurveyAnalysis, string, SurveyAnalysis> {
    return this.db.table("analyses");
  }

  async getAnalysisBySurveyId(
    surveyId: string
  ): Promise<SurveyAnalysis | undefined> {
    return await this.analyses().get(surveyId);
  }

  async saveSurveyAnalysis(analysis: SurveyAnalysis): Promise<void> {
    console.info("Saving survey analysis", analysis);
    await this.analyses().put(analysis);
  }

  // Audiences

  // helper to provide better type safety
  audiences(): Table<Audience, string, Audience> {
    return this.db.table("audiences");
  }

  async getAllAudiences(): Promise<any[]> {
    const audiences = await this.audiences().toArray();
    console.info("Get all audiences", audiences);
    return audiences;
  }

  async getAudienceById(audienceId: string): Promise<any> {
    const audience = await this.audiences().get(audienceId);
    console.info("Get audience by id", audience);
    return audience;
  }

  async saveAudience(audience: any): Promise<void> {
    console.info("Saving audience", audience);
    await this.audiences().put(audience);
  }

  async deleteAudience(audienceId: string): Promise<void> {
    await this.audiences().delete(audienceId);
  }
}

export const storageService = new StorageService();
